import axios from "@axios";

import { getSiteIdsForGroups } from "@/utils/store";

const DEFAULT_STATE = {
  dates: [],
  sites: [],
  devices: [15, 16],
  metrics: [],
  pageTypes: [],
  pageTypeList: [],
  evolutions: [],
  siteGroups: [],
  totalEvents: 0,
  totalPosts: 0,
  totalEventsJ2: 0,
  totalPostsJ2: 0,
  issueModalVisibility: false,
  selectedIssueId: null,
  deleteIssueModalVisibility: false,
  issues: [],
  data: [],
  urlMonitors: [],
  urlMonitorStats: [],
};

export default {
  namespaced: true,
  state: {
    ...DEFAULT_STATE,
  },
  getters: {
    getDates: (state) => {
      // always return the dates ordered
      return state.dates[0] < state.dates[1]
        ? [state.dates[0], state.dates[1]]
        : [state.dates[1], state.dates[0]];
    },
    getIssueModalVisibility: (state) => state.issueModalVisibility,
    getSelectedIssueId: (state) => state.selectedIssueId,
    getDeleteIssueModalVisibility: (state) => state.deleteIssueModalVisibility,
    getIssues: (state) => {
      return [...state.issues]; // Returns a new array reference
    },
    getSites: (state) => state.sites,
    getMetrics: (state) => state.metrics,
    getPageTypes: (state) => state.pageTypes,
    getPageTypeList: (state) => state.pageTypeList,
    getEvolutions: (state) => state.evolutions,
    getSiteGroups: (state) => state.siteGroups,
    getDevices: (state) => state.devices,
    getTotalEvents: (state) => state.totalEvents,
    getTotalPosts: (state) => state.totalPosts,
    getTotalEventsJ2: (state) => state.totalEventsJ2,
    getTotalPostsJ2: (state) => state.totalPostsJ2,
    getData: (state) => state.data,
    getUrlMonitors: (state) => state.urlMonitors,
    getUrlMonitorUrls: (state) =>
      state.urlMonitors.map((monitor) => monitor.url),
    getUrlMonitorStats: (state) => state.urlMonitorStats,
  },
  mutations: {
    initState(state) {
      for (const [key, value] of Object.entries(DEFAULT_STATE)) {
        state[key] = value;
      }
    },
    setDates(state, payload) {
      state.dates = payload;
    },
    setIssueModalVisibility(state, payload) {
      state.issueModalVisibility = payload;
    },
    setSelectedIssueId(state, payload) {
      state.selectedIssueId = payload;
    },
    setDeleteIssueModalVisibility(state, payload) {
      state.deleteIssueModalVisibility = payload;
    },
    setIssues(state, payload) {
      state.issues = payload;
    },
    setSites(state, payload) {
      state.sites = payload;
    },
    setMetrics(state, payload) {
      state.metrics = payload;
    },
    setPageTypes(state, payload) {
      state.pageTypes = payload;
    },
    setPageTypeList(state, payload) {
      state.pageTypeList = payload;
    },
    setEvolutions(state, payload) {
      state.evolutions = payload;
    },
    setSiteGroups(state, payload) {
      state.siteGroups = payload;
    },
    setTotalEvents(state, payload) {
      state.totalEvents = payload;
    },
    setTotalPosts(state, payload) {
      state.totalPosts = payload;
    },
    setTotalEventsJ2(state, payload) {
      state.totalEventsJ2 = payload;
    },
    setTotalPostsJ2(state, payload) {
      state.totalPostsJ2 = payload;
    },
    setData(state, payload) {
      state.data = payload;
    },
    setDevices(state, payload) {
      state.devices = payload;
    },
    setUrlMonitors(state, payload) {
      state.urlMonitors = payload;
    },
    setUrlMonitorStats(state, payload) {
      state.urlMonitorStats = payload;
    },
    addUrlMonitor(state, payload) {
      state.urlMonitors.push(payload);
    },
    setUrlMonitor(state, payload) {
      const index = state.urlMonitors.findIndex(
        (monitor) => monitor.id === payload.id
      );
      if (index !== -1) {
        state.urlMonitors.splice(index, 1, payload);
      }
    },
    removeUrlMonitor(state, id) {
      const index = state.urlMonitors.findIndex((monitor) => monitor.id === id);
      if (index !== -1) {
        state.urlMonitors.splice(index, 1);
      }
    },
  },
  actions: {
    initState({ commit }) {
      commit("initState");
    },
    updateDates({ commit }, payload) {
      if (payload.length >= 2) {
        commit("setDates", payload);
      }
    },
    updateIssueModalVisibility({ commit }, payload) {
      commit("setIssueModalVisibility", payload);
    },
    updateSelectedIssueId({ commit }, payload) {
      commit("setSelectedIssueId", payload);
    },
    updateDeleteIssueModalVisibility({ commit }, payload) {
      commit("setDeleteIssueModalVisibility", payload);
    },
    updateSites({ commit }, payload) {
      commit("setSites", payload);
    },
    updateMetrics({ commit }, payload) {
      commit("setMetrics", payload);
    },
    updatePageTypes({ commit }, payload) {
      commit("setPageTypes", payload);
    },
    updateEvolutions({ commit }, payload) {
      commit("setEvolutions", payload);
    },
    async updateSiteGroups({ commit }, payload) {
      commit("setSiteGroups", payload);

      const site_ids = await getSiteIdsForGroups(payload);
      commit("setSites", site_ids);

      return site_ids;
    },
    updateTotalEvents({ commit }, payload) {
      commit("setTotalEvents", payload);
    },
    updateTotalPosts({ commit }, payload) {
      commit("setTotalPosts", payload);
    },
    updateTotalEventsJ2({ commit }, payload) {
      commit("setTotalEventsJ2", payload);
    },
    updateTotalPostsJ2({ commit }, payload) {
      commit("setTotalPostsJ2", payload);
    },
    updateData({ commit }, payload) {
      commit("setData", payload);
    },
    updateDevices({ commit }, payload) {
      commit("setDevices", payload);
    },
    async getPageTypeList({ commit }) {
      const {
        data: { items },
      } = await axios.get("/web-perf/content-types", {});
      // console.log(data);
      commit("setPageTypeList", items);
      return items;
    },
    async getIssues({ commit, state }, payload) {
      const params = {};
      if (state.sites && state.sites.length > 0) {
        params.sites = state.sites.join(",");
      }
      if (state.pageTypes && state.pageTypes.length > 0) {
        params["content_types"] = state.pageTypes.join(",");
      }
      if (state.devices && state.devices.length > 0) {
        params.devices = state.devices.join(",");
      }
      if (state.metrics && state.metrics.length > 0) {
        params.metrics = state.metrics.join(",");
      }
      const { data } = await axios.get("/web-perf/cwv-issues", {
        params,
      });
      commit("setIssues", data.items);
    },
    async getUrlMonitors({ commit }) {
      try {
        const response = await axios.get("/url-monitors/");
        const monitors = response.data.results || response.data;
        commit("setUrlMonitors", monitors);
        return monitors;
      } catch (error) {
        console.error("Error fetching URL monitors:", error);
        throw error;
      }
    },
    async createUrlMonitor({ commit }, payload) {
      try {
        const response = await axios.post("/url-monitors/", payload);
        commit("addUrlMonitor", response.data);
        return response.data;
      } catch (error) {
        console.error("Error creating URL monitor:", error);
        throw error;
      }
    },
    async updateUrlMonitor({ commit }, { id, payload }) {
      try {
        const response = await axios.put(`/url-monitors/${id}`, payload);
        commit("setUrlMonitor", response.data);
        return response.data;
      } catch (error) {
        console.error("Error updating URL monitor:", error);
        throw error;
      }
    },
    async deleteUrlMonitor({ commit }, id) {
      try {
        await axios.delete(`/url-monitors/${id}`);
        commit("removeUrlMonitor", id);
        return true;
      } catch (error) {
        console.error("Error deleting URL monitor:", error);
        throw error;
      }
    },
    async getUrlMonitorStats({ commit }) {
      try {
        const response = await axios.get("/url-monitors/stats");
        const stats = response.data.data || response.data;
        commit("setUrlMonitorStats", stats);
        return stats;
      } catch (error) {
        console.error("Error fetching URL monitor stats:", error);
        throw error;
      }
    },
  },
};
